import { EventTimeline, Room } from 'matrix-js-sdk/src/matrix';

import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';

export interface Member {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  inviteExpiration?: number;
  roleId?: string;
  roleName: string;
  defaultRoleType?: DefaultRoleTypesEnum;
  powerLevel: number;
  isPendingInvite?: boolean;
}

const getMemberSortKey = ({ name, email, phone }: Member): string => (name || email || phone || '').toLowerCase();

export const sortMembers = (members: Member[]): Member[] =>
  [...members].sort((a, b) => {
    const powerLevelA = a.powerLevel ?? 0;
    const powerLevelB = b.powerLevel ?? 0;
    if (powerLevelB !== powerLevelA) return powerLevelB - powerLevelA;
    return getMemberSortKey(a).localeCompare(getMemberSortKey(b));
  });

type GetInviteableWorkspaceMembersParams = {
  members: Member[];
  myUserId: string | null | undefined;
  excludeUserIds?: ReadonlySet<string>;
  isGuestServerMember?: (userId: string) => boolean;
};

/** Joined workspace members for invite pickers — same filter + sort as the settings members list. */
export const getInviteableWorkspaceMembers = ({
  members,
  myUserId,
  excludeUserIds,
  isGuestServerMember = () => false,
}: GetInviteableWorkspaceMembersParams): Member[] =>
  sortMembers(
    members.filter(
      (m) =>
        !m.isPendingInvite && m.id !== myUserId && !isGuestServerMember(m.id) && !(excludeUserIds?.has(m.id) ?? false)
    )
  );

/**
 * Checks if a user can send a state event of the given type in the room.
 */
export const canSendStateEvent = (room: Room | null, userId: string | null, eventType: string): boolean => {
  if (!room || !userId) return false;

  const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  return state?.maySendStateEvent(eventType, userId) ?? false;
};
